import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Constants for form options
const STARTUP_STAGES = [
  { value: 'idea', label: 'Idea/Pre-product' },
  { value: 'mvp', label: 'MVP built' },
  { value: 'traction', label: 'Early traction' },
  { value: 'scaling', label: 'Scaling' },
];

const CAPITAL_RANGES = [
  { value: '<250k', label: '< USD 250k' },
  { value: '250k-500k', label: 'USD 250k–500k' },
  { value: '500k-1m', label: 'USD 500k–1m' },
  { value: '1m+', label: 'USD 1m+' },
  { value: 'undecided', label: 'Undecided/still assessing' },
];

const COFOUNDER_TYPES = [
  { value: 'technical', label: 'Technical/Product' },
  { value: 'commercial', label: 'Commercial/Growth' },
  { value: 'operations', label: 'Operations/Finance' },
];

const COFOUNDER_INVOLVEMENT = [
  { value: 'fulltime', label: 'Full-time' },
  { value: 'parttime', label: 'Part-time' },
  { value: 'advisory', label: 'Advisory' },
];

const COFOUNDER_AVAILABILITY = [
  { value: 'fulltime', label: 'Full-time' },
  { value: 'parttime', label: 'Part-time' },
  { value: 'advisory', label: 'Advisory/Flexible' },
];

const COFOUNDER_LOOKING_FOR = [
  { value: 'joining', label: 'Joining an early-stage startup' },
  { value: 'partnering', label: 'Partnering with a founder' },
  { value: 'open', label: 'Open to discussion' },
];

const INVESTOR_TYPES = [
  { value: 'angel', label: 'Angel' },
  { value: 'family-office', label: 'Family Office' },
  { value: 'vc', label: 'VC' },
  { value: 'syndicate', label: 'Syndicate/Club' },
  { value: 'other', label: 'Other' },
];

const CHEQUE_SIZES = [
  { value: '<50k', label: '< USD 50k' },
  { value: '50k-150k', label: 'USD 50k–150k' },
  { value: '150k-500k', label: 'USD 150k–500k' },
  { value: '500k+', label: 'USD 500k+' },
];

const INVESTOR_INTERESTS = [
  { value: 'early-stage', label: 'Early-stage founders' },
  { value: 'cofounder-matching', label: 'Co-founder matching' },
  { value: 'equity-opportunities', label: 'Equity-based opportunities' },
];

export default function WaitlistForm({ initialRole = null }) {
  const [role, setRole] = useState(initialRole || '');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [consent, setConsent] = useState(false);
  const [intentNote, setIntentNote] = useState('');
  
  // Founder fields
  const [companyName, setCompanyName] = useState('');
  const [startupStage, setStartupStage] = useState('');
  const [founderLookingFor, setFounderLookingFor] = useState([]);
  const [capitalRange, setCapitalRange] = useState('');
  const [cofounderTypeNeeded, setCofounderTypeNeeded] = useState([]);
  const [cofounderInvolvement, setCofounderInvolvement] = useState('');
  
  // Co-founder fields
  const [cofounderSkillset, setCofounderSkillset] = useState([]);
  const [cofounderAvailability, setCofounderAvailability] = useState('');
  const [cofounderLookingFor, setCofounderLookingFor] = useState([]);
  
  // Investor fields
  const [investorType, setInvestorType] = useState('');
  const [chequeSize, setChequeSize] = useState('');
  const [investorInterest, setInvestorInterest] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const successRef = useRef(null);
  const formStartFiredRef = useRef(false);

  /**
   * Track form_start event - fires once on first interaction
   * Reads origin data from sessionStorage (set by WaitlistCTA)
   */
  const trackFormStart = useCallback(() => {
    if (formStartFiredRef.current) return; // Fire only once per form session
    
    formStartFiredRef.current = true;
    
    // Read attribution data from sessionStorage
    const origin_path = sessionStorage.getItem('waitlist_origin_path') || window.location.pathname;
    const origin_url = sessionStorage.getItem('waitlist_origin_url') || window.location.href;
    const cta_role = sessionStorage.getItem('waitlist_cta_role') || '';
    
    // Push form_start event to dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form_start',
      form_name: 'waitlist',
      origin_path: origin_path,
      origin_url: origin_url,
      cta_role: cta_role,
      selected_role: role || ''
    });
  }, [role]);

  // Listen for role change events from CTAs
  useEffect(() => {
    const handleSetRole = (event) => {
      setRole(event.detail);
    };
    window.addEventListener('setWaitlistRole', handleSetRole);
    return () => window.removeEventListener('setWaitlistRole', handleSetRole);
  }, []);

  // Update role when initialRole prop changes
  useEffect(() => {
    if (initialRole) {
      setRole(initialRole);
    }
  }, [initialRole]);

  // Scroll to success message when shown
  useEffect(() => {
    if (success && successRef.current) {
      successRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Fire analytics event
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'waitlist_submit' });
      }
    }
  }, [success]);

  const handleCheckboxChange = (value, currentValues, setter) => {
    if (currentValues.includes(value)) {
      setter(currentValues.filter(v => v !== value));
    } else {
      setter([...currentValues, value]);
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!email) newErrors.email = 'Email is required';
    if (!fullName) newErrors.fullName = 'Full name is required';
    if (!role) newErrors.role = 'Please select a role';
    if (!consent) newErrors.consent = 'You must agree to continue';

    if (role === 'Founder') {
      if (!startupStage) newErrors.startupStage = 'Startup stage is required';
      if (founderLookingFor.length === 0) newErrors.founderLookingFor = 'Please select what you\'re looking for';
      
      // Capital range required if seeking capital
      if ((founderLookingFor.includes('capital') || founderLookingFor.includes('both')) && !capitalRange) {
        newErrors.capitalRange = 'Capital range is required';
      }
      // Co-founder type required if seeking co-founder
      if ((founderLookingFor.includes('cofounder') || founderLookingFor.includes('both')) && cofounderTypeNeeded.length === 0) {
        newErrors.cofounderTypeNeeded = 'Co-founder type is required';
      }
    }

    if (role === 'Co-founder') {
      if (cofounderSkillset.length === 0) newErrors.cofounderSkillset = 'Primary skillset is required';
      if (!cofounderAvailability) newErrors.cofounderAvailability = 'Availability is required';
      if (cofounderLookingFor.length === 0) newErrors.cofounderLookingFor = 'Please select what you\'re looking for';
    }

    if (role === 'Investor') {
      if (!investorType) newErrors.investorType = 'Investor type is required';
      if (!chequeSize) newErrors.chequeSize = 'Typical cheque size is required';
      if (investorInterest.length === 0) newErrors.investorInterest = 'Primary interest is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        email: email.toLowerCase(),
        role,
        full_name: fullName,
        consent,
        consent_text: "I agree to the collection and use of my personal data in accordance with the Privacy Policy.",
        consent_version: "v1",
        consent_source_url: window.location.href,  // Capture the page URL for compliance
        intent_note: intentNote || null,
      };

      if (role === 'Founder') {
        payload.company_name = companyName || null;
        payload.startup_stage = startupStage;
        payload.founder_looking_for = founderLookingFor;
        payload.capital_range = capitalRange || null;
        payload.cofounder_type_needed = cofounderTypeNeeded;
        payload.cofounder_involvement = cofounderInvolvement || null;
      } else if (role === 'Co-founder') {
        payload.cofounder_skillset = cofounderSkillset;
        payload.cofounder_availability = cofounderAvailability;
        payload.cofounder_looking_for = cofounderLookingFor;
      } else if (role === 'Investor') {
        payload.investor_type = investorType;
        payload.cheque_size = chequeSize;
        payload.investor_interest = investorInterest;
      }

      await axios.post(`${API}/waitlist/submit`, payload);
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getIntentPlaceholder = () => {
    if (role === 'Founder') return "Anything else you'd like us to know about what you're building or looking for?";
    if (role === 'Co-founder') return "Anything else about the type of opportunity or founder you're looking to partner with?";
    if (role === 'Investor') return "Anything else about your investment focus or the types of founders you'd like to meet?";
    return '';
  };

  if (success) {
    return (
      <div ref={successRef} className="max-w-lg mx-auto">
        <Card className="bg-white border-slate-200 p-8 text-center shadow-lg" data-testid="waitlist-success">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={40} />
          </div>
          <h3 className="text-2xl font-bold text-[#1e3a5f] mb-4">You're on the SeedSynch waitlist</h3>
          <div className="text-[#334d6e] text-sm space-y-3">
            <p>Thanks for joining.</p>
            <p>SeedSynch access is private and opens gradually.</p>
            {role === 'Investor' ? (
              <p>Investor access is invitation-led and configured at the organization level during onboarding. We'll be in touch when your access is ready.</p>
            ) : (
              <p>You'll be among the first to hear when the platform goes live and access becomes available.</p>
            )}
          </div>
        </Card>
      </div>
    );
  }

  const getSubmitButtonText = () => {
    if (role === 'Founder' || role === 'Co-founder') return 'Join The Wait List';
    if (role === 'Investor') return 'Request Early Access';
    return 'Submit';
  };

  const getMicroCopy = () => {
    if (role === 'Founder' || role === 'Co-founder') return "Founders join individually and are notified as private access opens.";
    if (role === 'Investor') return "Investor access is granted at the organization or office level during onboarding.";
    return '';
  };

  return (
    <Card className="bg-white border-slate-200 border p-6 sm:p-8 max-w-xl mx-auto shadow-lg" data-testid="waitlist-form">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Full Name */}
        <div>
          <Label htmlFor="fullName" className="text-[#1e3a5f] text-sm font-medium">Full name *</Label>
          <Input
            id="fullName"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            onFocus={trackFormStart}
            placeholder="Your full name"
            className={`mt-1.5 bg-white border-slate-300 text-[#1e3a5f] placeholder:text-slate-400 ${errors.fullName ? 'border-red-500' : ''}`}
            data-testid="waitlist-fullname"
          />
          {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email" className="text-[#1e3a5f] text-sm font-medium">Email *</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={trackFormStart}
            placeholder="you@company.com"
            className={`mt-1.5 bg-white border-slate-300 text-[#1e3a5f] placeholder:text-slate-400 ${errors.email ? 'border-red-500' : ''}`}
            data-testid="waitlist-email"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        {/* Role Selector */}
        <div>
          <Label htmlFor="role" className="text-[#1e3a5f] text-sm font-medium">Role *</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className={`mt-1.5 bg-white border-slate-300 text-[#1e3a5f] ${errors.role ? 'border-red-500' : ''}`} data-testid="waitlist-role">
              <SelectValue placeholder="Select your role" className="text-slate-400" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="Founder" className="text-[#1e3a5f] hover:bg-slate-100">Founder</SelectItem>
              <SelectItem value="Co-founder" className="text-[#1e3a5f] hover:bg-slate-100">Co-founder</SelectItem>
              <SelectItem value="Investor" className="text-[#1e3a5f] hover:bg-slate-100">Investor</SelectItem>
            </SelectContent>
          </Select>
          {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role}</p>}
        </div>

        {/* =============== FOUNDER FIELDS =============== */}
        {role === 'Founder' && (
          <>
            {/* Company Name (optional) */}
            <div>
              <Label htmlFor="companyName" className="text-[#1e3a5f] text-sm font-medium">Company name <span className="text-slate-400">(optional)</span></Label>
              <Input
                id="companyName"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Your startup name"
                className="mt-1.5 bg-white border-slate-300 text-[#1e3a5f] placeholder:text-slate-400"
              />
            </div>

            {/* Startup Stage */}
            <div>
              <Label className="text-[#1e3a5f] text-sm font-medium">Current stage *</Label>
              <Select value={startupStage} onValueChange={setStartupStage}>
                <SelectTrigger className={`mt-1.5 bg-white border-slate-300 text-[#1e3a5f] ${errors.startupStage ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {STARTUP_STAGES.map(s => (
                    <SelectItem key={s.value} value={s.value} className="text-[#1e3a5f] hover:bg-slate-100">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.startupStage && <p className="text-red-500 text-xs mt-1">{errors.startupStage}</p>}
            </div>

            {/* Looking For (multi-checkbox) */}
            <div>
              <Label className="text-[#1e3a5f] text-sm font-medium mb-2 block">Looking for *</Label>
              <div className="space-y-2">
                {[
                  { value: 'capital', label: 'Capital partner(s)' },
                  { value: 'cofounder', label: 'Co-founder' },
                  { value: 'both', label: 'Both' },
                ].map(item => (
                  <div key={item.value} className="flex items-center gap-3">
                    <Checkbox
                      id={`founder-looking-${item.value}`}
                      checked={founderLookingFor.includes(item.value)}
                      onCheckedChange={() => handleCheckboxChange(item.value, founderLookingFor, setFounderLookingFor)}
                      className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <Label htmlFor={`founder-looking-${item.value}`} className="text-[#334d6e] text-sm cursor-pointer">{item.label}</Label>
                  </div>
                ))}
              </div>
              {errors.founderLookingFor && <p className="text-red-500 text-xs mt-1">{errors.founderLookingFor}</p>}
            </div>

            {/* Capital Range (conditional) */}
            {(founderLookingFor.includes('capital') || founderLookingFor.includes('both')) && (
              <div>
                <Label className="text-[#1e3a5f] text-sm font-medium">Capital range *</Label>
                <Select value={capitalRange} onValueChange={setCapitalRange}>
                  <SelectTrigger className={`mt-1.5 bg-white border-slate-300 text-[#1e3a5f] ${errors.capitalRange ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {CAPITAL_RANGES.map(r => (
                      <SelectItem key={r.value} value={r.value} className="text-[#1e3a5f] hover:bg-slate-100">{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.capitalRange && <p className="text-red-500 text-xs mt-1">{errors.capitalRange}</p>}
              </div>
            )}

            {/* Co-founder Type Needed (conditional) */}
            {(founderLookingFor.includes('cofounder') || founderLookingFor.includes('both')) && (
              <>
                <div>
                  <Label className="text-[#1e3a5f] text-sm font-medium mb-2 block">Co-founder type needed *</Label>
                  <div className="space-y-2">
                    {COFOUNDER_TYPES.map(item => (
                      <div key={item.value} className="flex items-center gap-3">
                        <Checkbox
                          id={`cofounder-type-${item.value}`}
                          checked={cofounderTypeNeeded.includes(item.value)}
                          onCheckedChange={() => handleCheckboxChange(item.value, cofounderTypeNeeded, setCofounderTypeNeeded)}
                          className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                        />
                        <Label htmlFor={`cofounder-type-${item.value}`} className="text-[#334d6e] text-sm cursor-pointer">{item.label}</Label>
                      </div>
                    ))}
                  </div>
                  {errors.cofounderTypeNeeded && <p className="text-red-500 text-xs mt-1">{errors.cofounderTypeNeeded}</p>}
                </div>

                <div>
                  <Label className="text-[#1e3a5f] text-sm font-medium">Co-founder involvement <span className="text-slate-400">(optional)</span></Label>
                  <Select value={cofounderInvolvement} onValueChange={setCofounderInvolvement}>
                    <SelectTrigger className="mt-1.5 bg-white border-slate-300 text-[#1e3a5f]">
                      <SelectValue placeholder="Select involvement level" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {COFOUNDER_INVOLVEMENT.map(i => (
                        <SelectItem key={i.value} value={i.value} className="text-[#1e3a5f] hover:bg-slate-100">{i.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </>
        )}

        {/* =============== CO-FOUNDER FIELDS =============== */}
        {role === 'Co-founder' && (
          <>
            {/* Primary Skillset */}
            <div>
              <Label className="text-[#1e3a5f] text-sm font-medium mb-2 block">Primary skillset *</Label>
              <div className="space-y-2">
                {COFOUNDER_TYPES.map(item => (
                  <div key={item.value} className="flex items-center gap-3">
                    <Checkbox
                      id={`skillset-${item.value}`}
                      checked={cofounderSkillset.includes(item.value)}
                      onCheckedChange={() => handleCheckboxChange(item.value, cofounderSkillset, setCofounderSkillset)}
                      className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <Label htmlFor={`skillset-${item.value}`} className="text-[#334d6e] text-sm cursor-pointer">{item.label}</Label>
                  </div>
                ))}
              </div>
              {errors.cofounderSkillset && <p className="text-red-500 text-xs mt-1">{errors.cofounderSkillset}</p>}
            </div>

            {/* Availability */}
            <div>
              <Label className="text-[#1e3a5f] text-sm font-medium">Availability *</Label>
              <Select value={cofounderAvailability} onValueChange={setCofounderAvailability}>
                <SelectTrigger className={`mt-1.5 bg-white border-slate-300 text-[#1e3a5f] ${errors.cofounderAvailability ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Select availability" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {COFOUNDER_AVAILABILITY.map(a => (
                    <SelectItem key={a.value} value={a.value} className="text-[#1e3a5f] hover:bg-slate-100">{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.cofounderAvailability && <p className="text-red-500 text-xs mt-1">{errors.cofounderAvailability}</p>}
            </div>

            {/* Looking For */}
            <div>
              <Label className="text-[#1e3a5f] text-sm font-medium mb-2 block">Looking for *</Label>
              <div className="space-y-2">
                {COFOUNDER_LOOKING_FOR.map(item => (
                  <div key={item.value} className="flex items-center gap-3">
                    <Checkbox
                      id={`cf-looking-${item.value}`}
                      checked={cofounderLookingFor.includes(item.value)}
                      onCheckedChange={() => handleCheckboxChange(item.value, cofounderLookingFor, setCofounderLookingFor)}
                      className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <Label htmlFor={`cf-looking-${item.value}`} className="text-[#334d6e] text-sm cursor-pointer">{item.label}</Label>
                  </div>
                ))}
              </div>
              {errors.cofounderLookingFor && <p className="text-red-500 text-xs mt-1">{errors.cofounderLookingFor}</p>}
            </div>
          </>
        )}

        {/* =============== INVESTOR FIELDS =============== */}
        {role === 'Investor' && (
          <>
            {/* Investor Type */}
            <div>
              <Label className="text-[#1e3a5f] text-sm font-medium">Investor type *</Label>
              <Select value={investorType} onValueChange={setInvestorType}>
                <SelectTrigger className={`mt-1.5 bg-white border-slate-300 text-[#1e3a5f] ${errors.investorType ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {INVESTOR_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-[#1e3a5f] hover:bg-slate-100">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.investorType && <p className="text-red-500 text-xs mt-1">{errors.investorType}</p>}
            </div>

            {/* Cheque Size */}
            <div>
              <Label className="text-[#1e3a5f] text-sm font-medium">Typical cheque size *</Label>
              <Select value={chequeSize} onValueChange={setChequeSize}>
                <SelectTrigger className={`mt-1.5 bg-white border-slate-300 text-[#1e3a5f] ${errors.chequeSize ? 'border-red-500' : ''}`}>
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {CHEQUE_SIZES.map(c => (
                    <SelectItem key={c.value} value={c.value} className="text-[#1e3a5f] hover:bg-slate-100">{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.chequeSize && <p className="text-red-500 text-xs mt-1">{errors.chequeSize}</p>}
            </div>

            {/* Primary Interest */}
            <div>
              <Label className="text-[#1e3a5f] text-sm font-medium mb-2 block">Primary interest *</Label>
              <div className="space-y-2">
                {INVESTOR_INTERESTS.map(item => (
                  <div key={item.value} className="flex items-center gap-3">
                    <Checkbox
                      id={`interest-${item.value}`}
                      checked={investorInterest.includes(item.value)}
                      onCheckedChange={() => handleCheckboxChange(item.value, investorInterest, setInvestorInterest)}
                      className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <Label htmlFor={`interest-${item.value}`} className="text-[#334d6e] text-sm cursor-pointer">{item.label}</Label>
                  </div>
                ))}
              </div>
              {errors.investorInterest && <p className="text-red-500 text-xs mt-1">{errors.investorInterest}</p>}
            </div>
          </>
        )}

        {/* Intent Note (optional - all roles) */}
        {role && (
          <div>
            <Label htmlFor="intentNote" className="text-[#1e3a5f] text-sm font-medium">
              Additional context <span className="text-slate-400">(optional, max 300 chars)</span>
            </Label>
            <Textarea
              id="intentNote"
              value={intentNote}
              onChange={(e) => setIntentNote(e.target.value.slice(0, 300))}
              placeholder={getIntentPlaceholder()}
              className="mt-1.5 bg-white border-slate-300 text-[#1e3a5f] placeholder:text-slate-400 min-h-[80px]"
            />
            <p className="text-slate-400 text-xs mt-1 text-right">{intentNote.length}/300</p>
          </div>
        )}

        {/* Consent Checkbox */}
        {role && (
          <div className="flex items-start gap-3">
            <Checkbox
              id="consent"
              checked={consent}
              onCheckedChange={setConsent}
              className={`mt-0.5 border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 ${errors.consent ? 'border-red-500' : ''}`}
              data-testid="waitlist-consent"
            />
            <Label htmlFor="consent" className="text-[#334d6e] text-sm leading-tight cursor-pointer">
              I agree to the collection and use of my personal data in accordance with the{' '}
              <Link to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
            </Label>
          </div>
        )}
        {errors.consent && <p className="text-red-500 text-xs">{errors.consent}</p>}

        {/* Role-based micro-copy */}
        {role && (
          <p className="text-blue-700 text-xs bg-blue-50 p-3 rounded-md border border-blue-200">
            {getMicroCopy()}
          </p>
        )}

        {/* Submit Button */}
        {role && (
          <Button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-4 text-base font-semibold"
            data-testid="waitlist-submit"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={18} />
                Submitting...
              </>
            ) : (
              getSubmitButtonText()
            )}
          </Button>
        )}
      </form>
    </Card>
  );
}
